import {DeepReadonly} from 'ts-essentials'
import {LangType} from 'yougile-lang'
import {ObjUtil, Util} from 'yougile-util'
import {createDataObjectId, DataObjectId} from '../data-object'
import {HubData} from '../hubs'
import {
  permissionChildToObject,
  PermissionsById,
  ProjectChildPermission
} from './permission-object'
import {
  getDefaultProjectPermissions,
  ProjectPermissions
} from './project-permissions'
import {TaskPermissions} from './task-permissions'

// DataObjectId describes custom permission id
export type PermissionRoleKey = 'admin' | 'worker' | 'observer' | DataObjectId

export interface PermissionRole {
  key: PermissionRoleKey
  getName: (lang: LangType) => string
  getDescription: (lang: LangType) => string
  permissions: ProjectPermissions
}

export interface PermissionCustomRole {
  key: PermissionRoleKey
  name: string
  description: string
  permissions: ProjectPermissions
}

export const makeDefaultRole = (): PermissionCustomRole => ({
  key: createDataObjectId(),
  name: '',
  description: '',
  permissions: getDefaultProjectPermissions()
})

export const getRoleFromCustomRole = (
  role: PermissionCustomRole
): PermissionRole => {
  return {
    key: role.key,
    permissions: role.permissions,
    getName: _ => role.name,
    getDescription: _ => role.description
  }
}

const taskFullPermissions: TaskPermissions = {
  assignUsers: 'yes',
  close: true,
  connect: true,
  delete: true,
  editDescription: true,
  complete: true,
  editPins: true,
  editStickers: true,
  editSubtasks: 'yes',
  editTitle: true,
  editWhoToNotify: 'yes',
  move: 'yes',
  sendFiles: true,
  sendMessages: true,
  show: true
}

export const adminRole: DeepReadonly<PermissionRole> = {
  key: 'admin',
  getName: lang => lang.permission.adminRole,
  getDescription: lang => lang.permission.adminRoleDescription,
  permissions: {
    delete: true,
    editTitle: true,

    addBoard: true,
    boards: {
      delete: true,
      editStickers: true,
      editTitle: true,
      move: true,
      showStickers: true,

      addColumn: true,
      settings: true,
      columns: {
        delete: true,
        editTitle: true,
        move: 'yes',

        addTask: true,
        allTasks: taskFullPermissions
      }
    }
  }
}

export const workerRole: DeepReadonly<PermissionRole> = {
  key: 'worker',
  getName: lang => lang.permission.workerRole,
  getDescription: lang => lang.permission.workerRoleDescription,
  permissions: {
    delete: false,
    editTitle: false,

    addBoard: false,
    boards: {
      delete: false,
      editStickers: false,
      editTitle: false,
      move: false,
      settings: false,
      showStickers: true,

      addColumn: false,
      columns: {
        delete: false,
        editTitle: false,
        move: 'no',

        addTask: true,
        allTasks: {
          ...taskFullPermissions,
          complete: undefined
        }
      }
    }
  }
}

export const observerRole: DeepReadonly<PermissionRole> = {
  key: 'observer',
  getName: lang => lang.permission.observerRole,
  getDescription: lang => lang.permission.observerRoleDescription,
  permissions: {
    delete: false,
    editTitle: false,

    addBoard: false,
    boards: {
      delete: false,
      editStickers: false,
      editTitle: false,
      move: false,
      settings: false,
      showStickers: true,

      addColumn: false,
      columns: {
        delete: false,
        editTitle: false,
        move: 'no',

        addTask: false,
        allTasks: {
          assignUsers: 'no',
          close: false,
          connect: false,
          delete: false,
          editDescription: false,
          complete: false,
          editPins: false,
          editStickers: false,
          editSubtasks: 'no',
          editTitle: false,
          editWhoToNotify: 'self',
          move: 'no',
          sendFiles: true,
          sendMessages: true,
          show: true
        }
      }
    }
  }
}

export const getPermissionRoles = (): DeepReadonly<PermissionRole[]> => [
  adminRole,
  workerRole,
  observerRole
]

export const getPermissionFromSystemRole = (
  role: PermissionRoleKey
): ProjectPermissions | undefined => {
  if (!role) {
    return undefined
  }
  if (role === 'admin') {
    return adminRole.permissions
  } else if (role === 'worker') {
    return workerRole.permissions
  } else if (role === 'observer') {
    return observerRole.permissions
  } else {
    return undefined
  }
}

export const getPermissionFromRole = (
  project: {id: DataObjectId; data?: HubData} | undefined,
  roleKey: PermissionRoleKey | undefined,
  withSystem: boolean
): PermissionsById => {
  if (!project?.id || !roleKey) {
    return {}
  }
  if (roleKey === 'admin' || roleKey === 'worker' || roleKey === 'observer') {
    const perm = getPermissionFromSystemRole(roleKey)
    if (withSystem && perm) {
      return {
        [project.id]: {
          objectId: project.id,
          type: 'project',
          permissions: perm
        }
      }
    } else {
      return {}
    }
  } else {
    const projPerm = project?.data?.roles?.[roleKey]?.permissions
    if (!projPerm) {
      return {}
    }
    const permissions: PermissionsById = {
      [project.id]: {
        objectId: project.id,
        type: 'project',
        permissions: projPerm
      }
    }
    ObjUtil.forEach(projPerm?.children, (id: DataObjectId, perm) => {
      const p = permissionChildToObject(id, perm)
      if (p) {
        permissions[id] = p
      }
    })
    return permissions
  }
}

export const duplicateRole = (
  role: PermissionCustomRole
): PermissionCustomRole => {
  const dup = Util.clone(role)
  dup.key = createDataObjectId()
  return dup
}

export const relinkRolePermissionsChildren = (
  children: {[id: string]: ProjectChildPermission},
  oldNewMap: Map<DataObjectId, DataObjectId>
): typeof children => {
  const relinked: typeof children = {}

  for (const [oldKey, value] of Object.entries(children)) {
    const newKey = oldNewMap.get(oldKey as DataObjectId)
    relinked[newKey ?? oldKey] = value
  }

  return relinked
}
